import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { mainContentClassName } from '../context/NavContext';
import axios from 'axios';
import { defaultMealPlanConstraints, normalizeMealPlanConstraints } from '../utils/mealPlanConstraints';
import PlanningPreferencesEditor from '../components/PlanningPreferencesEditor';

function Profile() {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [activeTab, setActiveTab] = useState('account'); // 'account' or 'mealPlan'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [constraintsLoading, setConstraintsLoading] = useState(false);
  const formTopRef = useRef(null); // Ref for the top of the profile container

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // If password is provided, validate it
    if (password) {
      if (password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    setLoading(true);

    const result = await updateProfile(
      username !== user.username ? username : undefined,
      email !== user.email ? email : undefined,
      password || undefined
    );

    if (result.success) {
      setSuccess('Profile updated successfully!');
      setPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <>
      <div 
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/form-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
          minHeight: '100vh',
          height: '100%'
        }}
      />
      <Navigation />
      <div className={mainContentClassName}>
        <div className="max-w-5xl mx-auto">
          <div ref={formTopRef} className="bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Profile</h1>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b">
              <button
                type="button"
                onClick={() => setActiveTab('account')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  activeTab === 'account'
                    ? 'border-b-2 border-amber-600 text-amber-600'
                    : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                Account Settings
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('mealPlan')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  activeTab === 'mealPlan'
                    ? 'border-b-2 border-amber-600 text-amber-600'
                    : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                Meal Plan Constraints
              </button>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                {success}
              </div>
            )}

            {activeTab === 'account' && (
              <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  minLength={3}
                  maxLength={30}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="pt-4 border-t">
                <h3 className="text-lg font-semibold mb-4">Change Password (optional)</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={6}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Leave empty to keep current password"
                    />
                    {password && (
                      <p className="text-xs text-gray-500 mt-1">At least 6 characters</p>
                    )}
                  </div>

                  {password && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="Confirm new password"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md"
                >
                  {loading ? 'Updating...' : 'Update Profile'}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md"
                >
                  Logout
                </button>
              </div>
            </form>
            )}

            {activeTab === 'mealPlan' && (
              <>
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  <p className="mb-2">
                    These are your <strong>default</strong> constraints. Recipe usage tracking is configured here only.
                    When generating a meal plan, you can pick a saved preset or customize constraints for that run.
                  </p>
                  <Link to="/constraint-profiles" className="text-amber-700 font-semibold hover:underline">
                    Manage constraint presets →
                  </Link>
                </div>
                <MealPlanConstraintsEditor
                  user={user}
                  showUsageTracking
                  showDefaultMealTypes
                  onSuccess={() => {
                    setSuccess('Meal plan constraints updated successfully!');
                    setTimeout(() => setSuccess(''), 3000);
                  }}
                  scrollToTop={() => {
                    if (formTopRef.current) {
                      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                />
              </>
            )}

            <div className="mt-6 pt-6 border-t text-sm text-gray-600">
              <p>Member since: {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function MealPlanConstraintsEditor({
  user,
  value: controlledValue,
  onChange: controlledOnChange,
  showUsageTracking = false,
  showDefaultMealTypes = true,
  embedded = false,
  onSuccess,
  scrollToTop
}) {
  const { updateProfile } = useAuth();
  const [internalData, setInternalData] = useState(defaultMealPlanConstraints());
  const formData = controlledValue ?? internalData;
  const setFormData = controlledOnChange ?? setInternalData;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [selectedMealTypePeriod, setSelectedMealTypePeriod] = useState('week'); // For dynamic period selection
  const [selectedPortionPeriod, setSelectedPortionPeriod] = useState('week'); // For dynamic portion period selection
  const [mealTypeMode, setMealTypeMode] = useState('count'); // 'count' or 'portions'
  const ruleRefs = useRef({}); // Refs for each rule element

  // Available categories and meat types
  const categories = ['quick', 'fish', 'vegetarian', 'pasta', 'rice', 'potatoes', 'soup', 'seafood'];
  const meatTypes = ['chicken', 'beef', 'pork', 'turkey', 'lamb'];

  useEffect(() => {
    // Fetch available tags and recipes
    const fetchTagsAndRecipes = async () => {
      try {
        const response = await axios.get(`/api/recipes`);
        const allTags = [...new Set(response.data.flatMap(r => r.tags || []))].sort();
        setAvailableTags(allTags);
        setAvailableRecipes(response.data || []);
        setLoadingTags(false);
      } catch (error) {
        console.error('Error fetching tags and recipes:', error);
        setLoadingTags(false);
      }
    };
    fetchTagsAndRecipes();
  }, []);

  useEffect(() => {
    if (controlledValue || !user?.mealPlanConstraints) return;
    setInternalData(normalizeMealPlanConstraints(user.mealPlanConstraints));
  }, [user, controlledValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Scroll to top immediately
    if (scrollToTop) {
      scrollToTop();
    }

    try {
      const result = await updateProfile(undefined, undefined, undefined, formData);
      if (result.success) {
        onSuccess();
        // Scroll again after success to ensure we're at the top
        if (scrollToTop) {
          setTimeout(() => scrollToTop(), 100);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update constraints');
    } finally {
      setLoading(false);
    }
  };

  const addRule = () => {
    setFormData(prev => {
      const newRules = [...prev.customRules, {
        target: '',
        targetType: 'tag',
        constraintType: 'minimum',
        value: 1,
        period: 'week',
        mealTypes: [] // Optional: empty means applies to all meal types
      }];
      
      // Scroll to the bottom of the newly added rule after state update
      setTimeout(() => {
        const newIndex = newRules.length - 1;
        const ruleElement = ruleRefs.current[`rule-${newIndex}`];
        if (ruleElement) {
          ruleElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'end' 
          });
          // Optionally focus the first input in the new rule
          const firstSelect = ruleElement.querySelector('select');
          if (firstSelect) {
            setTimeout(() => firstSelect.focus(), 300);
          }
        }
      }, 100);
      
      return {
        ...prev,
        customRules: newRules
      };
    });
  };

  const removeRule = (index) => {
    setFormData(prev => ({
      ...prev,
      customRules: prev.customRules.filter((_, i) => i !== index)
    }));
  };

  const updateRule = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customRules: prev.customRules.map((rule, i) => 
        i === index ? { ...rule, [field]: value } : rule
      )
    }));
  };

  const getTargetOptions = (targetType) => {
    if (targetType === 'tag') {
      return availableTags;
    } else if (targetType === 'category') {
      return categories;
    } else if (targetType === 'meatType') {
      return meatTypes;
    } else if (targetType === 'recipe') {
      return availableRecipes;
    } else if (targetType === 'mealType') {
      return ['breakfast', 'lunch', 'dinner', 'snack'];
    }
    return [];
  };

  const editorContent = (
    <div className="space-y-6">
      {!embedded && error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {showDefaultMealTypes && (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Default Meal Types</label>
        <div className="flex flex-wrap gap-4">
          {['breakfast', 'lunch', 'dinner', 'snack'].map(type => (
            <label key={type} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.defaultMealTypes.includes(type)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({ ...formData, defaultMealTypes: [...formData.defaultMealTypes, type] });
                  } else {
                    setFormData({ ...formData, defaultMealTypes: formData.defaultMealTypes.filter(t => t !== type) });
                  }
                }}
                className="mr-2"
              />
              <span className="capitalize">{type}</span>
            </label>
          ))}
        </div>
      </div>
      )}

      {showUsageTracking && (
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-4">Recipe Usage Tracking</h3>
        <p className="text-sm text-gray-600 mb-4">
          Set how far back to track recipe usage. Recipes not used in this period will be prioritized when generating meal plans.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm font-medium text-gray-700">Track recipe usage for the last</label>
          <input
            type="number"
            min="1"
            value={formData.globalRules.usageTrackingPeriod?.value || 2}
            onChange={(e) => {
              setFormData({
                ...formData,
                globalRules: {
                  ...formData.globalRules,
                  usageTrackingPeriod: {
                    ...formData.globalRules.usageTrackingPeriod,
                    value: parseInt(e.target.value) || 2
                  }
                }
              });
            }}
            className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm font-semibold text-center focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
          <select
            value={formData.globalRules.usageTrackingPeriod?.unit || 'weeks'}
            onChange={(e) => {
              setFormData({
                ...formData,
                globalRules: {
                  ...formData.globalRules,
                  usageTrackingPeriod: {
                    ...formData.globalRules.usageTrackingPeriod,
                    unit: e.target.value
                  }
                }
              });
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-white hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="days">days</option>
            <option value="weeks">weeks</option>
            <option value="months">months</option>
          </select>
        </div>
      </div>
      )}

      <PlanningPreferencesEditor
        value={formData.planningPreferences}
        onChange={(planningPreferences) => setFormData({ ...formData, planningPreferences })}
      />

      {/* Max Total Recipes */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-4">Max Total Recipes Per Period</h3>
        <p className="text-sm text-gray-600 mb-4">
          Set the maximum number of recipes (total) that can be planned per period. Leave empty for no limit.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max recipes per week</label>
            <input
              type="number"
              min="1"
              value={formData.globalRules.maxRecipesPerWeek || ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  globalRules: {
                    ...formData.globalRules,
                    maxRecipesPerWeek: e.target.value ? parseInt(e.target.value) : null
                  }
                });
              }}
              placeholder="No limit"
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max recipes per month</label>
            <input
              type="number"
              min="1"
              value={formData.globalRules.maxRecipesPerMonth || ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  globalRules: {
                    ...formData.globalRules,
                    maxRecipesPerMonth: e.target.value ? parseInt(e.target.value) : null
                  }
                });
              }}
              placeholder="No limit"
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max recipes per year</label>
            <input
              type="number"
              min="1"
              value={formData.globalRules.maxRecipesPerYear || ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  globalRules: {
                    ...formData.globalRules,
                    maxRecipesPerYear: e.target.value ? parseInt(e.target.value) : null
                  }
                });
              }}
              placeholder="No limit"
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
        </div>
      </div>

      {/* Total Portions */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-2">Total Portions Per Period</h3>
        <p className="text-sm text-gray-600 mb-4">
          Set minimum and/or maximum total portions (sum of all recipe servings) per period. Leave empty for no limit.
        </p>
        <div className="flex gap-2 mb-4">
          {['week', 'month', 'year'].map(period => (
            <button
              key={period}
              type="button"
              onClick={() => setSelectedPortionPeriod(period)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                selectedPortionPeriod === period
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min portions</label>
            <input
              type="number"
              min="1"
              value={formData.globalRules[`minPortionsPer${selectedPortionPeriod.charAt(0).toUpperCase() + selectedPortionPeriod.slice(1)}`] || ''}
              onChange={(e) => {
                const fieldName = `minPortionsPer${selectedPortionPeriod.charAt(0).toUpperCase() + selectedPortionPeriod.slice(1)}`;
                setFormData({
                  ...formData,
                  globalRules: {
                    ...formData.globalRules,
                    [fieldName]: e.target.value ? parseInt(e.target.value) : null
                  }
                });
              }}
              placeholder="No minimum"
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max portions</label>
            <input
              type="number"
              min="1"
              value={formData.globalRules[`maxPortionsPer${selectedPortionPeriod.charAt(0).toUpperCase() + selectedPortionPeriod.slice(1)}`] || ''}
              onChange={(e) => {
                const fieldName = `maxPortionsPer${selectedPortionPeriod.charAt(0).toUpperCase() + selectedPortionPeriod.slice(1)}`;
                setFormData({
                  ...formData,
                  globalRules: {
                    ...formData.globalRules,
                    [fieldName]: e.target.value ? parseInt(e.target.value) : null
                  }
                });
              }}
              placeholder="No maximum"
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
        </div>
      </div>

      {/* Meal Type Counts/Portions */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-2">
          {mealTypeMode === 'count' ? 'Recipe Count Per Meal Type' : 'Portions Per Meal Type'}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {mealTypeMode === 'count' 
            ? 'Set minimum and/or maximum number of recipes for each meal type per period. Leave empty for no limit.'
            : 'Set minimum and/or maximum number of portions for each meal type per period. Leave empty for no limit.'}
        </p>
        <div className="flex gap-2 items-center mb-4">
          {/* Toggle between Count and Portions */}
          <div className="flex bg-gray-200 rounded-lg p-1 mr-2">
            <button
              type="button"
              onClick={() => setMealTypeMode('count')}
              className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${
                mealTypeMode === 'count'
                  ? 'bg-amber-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Count
            </button>
            <button
              type="button"
              onClick={() => setMealTypeMode('portions')}
              className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${
                mealTypeMode === 'portions'
                  ? 'bg-amber-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Portions
            </button>
          </div>
          {/* Period selector */}
          <div className="flex gap-2">
            {['week', 'month', 'year'].map(period => (
              <button
                key={period}
                type="button"
                onClick={() => setSelectedMealTypePeriod(period)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  selectedMealTypePeriod === period
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {['breakfast', 'lunch', 'dinner', 'snack'].map(mealType => (
            <div key={mealType} className="bg-gray-50 p-3 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">{mealType}</label>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Min</label>
                  <input
                    type="number"
                    min="0"
                    value={
                      mealTypeMode === 'count'
                        ? (formData.globalRules.mealTypeCounts?.[selectedMealTypePeriod]?.[mealType]?.min || '')
                        : (formData.globalRules.mealTypePortions?.[selectedMealTypePeriod]?.[mealType]?.min || '')
                    }
                    onChange={(e) => {
                      const value = e.target.value ? parseInt(e.target.value) : null;
                      if (mealTypeMode === 'count') {
                        const currentCounts = formData.globalRules.mealTypeCounts || {};
                        const currentPeriod = currentCounts[selectedMealTypePeriod] || {};
                        const currentMealType = currentPeriod[mealType] || { min: null, max: null };
                        setFormData({
                          ...formData,
                          globalRules: {
                            ...formData.globalRules,
                            mealTypeCounts: {
                              ...currentCounts,
                              [selectedMealTypePeriod]: {
                                ...currentPeriod,
                                [mealType]: {
                                  ...currentMealType,
                                  min: value
                                }
                              }
                            }
                          }
                        });
                      } else {
                        const currentPortions = formData.globalRules.mealTypePortions || {};
                        const currentPeriod = currentPortions[selectedMealTypePeriod] || {};
                        const currentMealType = currentPeriod[mealType] || { min: null, max: null };
                        setFormData({
                          ...formData,
                          globalRules: {
                            ...formData.globalRules,
                            mealTypePortions: {
                              ...currentPortions,
                              [selectedMealTypePeriod]: {
                                ...currentPeriod,
                                [mealType]: {
                                  ...currentMealType,
                                  min: value
                                }
                              }
                            }
                          }
                        });
                      }
                    }}
                    placeholder="No min"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Max</label>
                  <input
                    type="number"
                    min="0"
                    value={
                      mealTypeMode === 'count'
                        ? (formData.globalRules.mealTypeCounts?.[selectedMealTypePeriod]?.[mealType]?.max || '')
                        : (formData.globalRules.mealTypePortions?.[selectedMealTypePeriod]?.[mealType]?.max || '')
                    }
                    onChange={(e) => {
                      const value = e.target.value ? parseInt(e.target.value) : null;
                      if (mealTypeMode === 'count') {
                        const currentCounts = formData.globalRules.mealTypeCounts || {};
                        const currentPeriod = currentCounts[selectedMealTypePeriod] || {};
                        const currentMealType = currentPeriod[mealType] || { min: null, max: null };
                        setFormData({
                          ...formData,
                          globalRules: {
                            ...formData.globalRules,
                            mealTypeCounts: {
                              ...currentCounts,
                              [selectedMealTypePeriod]: {
                                ...currentPeriod,
                                [mealType]: {
                                  ...currentMealType,
                                  max: value
                                }
                              }
                            }
                          }
                        });
                      } else {
                        const currentPortions = formData.globalRules.mealTypePortions || {};
                        const currentPeriod = currentPortions[selectedMealTypePeriod] || {};
                        const currentMealType = currentPeriod[mealType] || { min: null, max: null };
                        setFormData({
                          ...formData,
                          globalRules: {
                            ...formData.globalRules,
                            mealTypePortions: {
                              ...currentPortions,
                              [selectedMealTypePeriod]: {
                                ...currentPeriod,
                                [mealType]: {
                                  ...currentMealType,
                                  max: value
                                }
                              }
                            }
                          }
                        });
                      }
                    }}
                    placeholder="No max"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recipe Preferences */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-4">Recipe Preferences</h3>
        <p className="text-sm text-gray-600 mb-4">
          Optional scoring preferences used when choosing between valid recipes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max cooking time (minutes)</label>
            <input
              type="number"
              min="1"
              value={formData.globalRules.maxDuration || ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  globalRules: {
                    ...formData.globalRules,
                    maxDuration: e.target.value ? parseInt(e.target.value) : null
                  }
                });
              }}
              placeholder="No limit"
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prefer minimum rating</label>
            <select
              value={formData.globalRules.preferRatings ?? ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  globalRules: {
                    ...formData.globalRules,
                    preferRatings: e.target.value ? parseInt(e.target.value) : null
                  }
                });
              }}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="">No preference</option>
              {[5, 4, 3, 2, 1].map((rating) => (
                <option key={rating} value={rating}>{rating} stars or higher</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prefer tags</label>
            {loadingTags ? (
              <span className="text-sm text-gray-400">Loading tags...</span>
            ) : (
              <select
                multiple
                value={formData.globalRules.preferTags || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                  setFormData({
                    ...formData,
                    globalRules: {
                      ...formData.globalRules,
                      preferTags: selected
                    }
                  });
                }}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 min-h-[80px]"
              >
                {availableTags.map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
          </div>
        </div>
      </div>

      {/* Custom Rules */}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Custom Rules</h3>
          <button
            type="button"
            onClick={addRule}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors text-sm"
          >
            + Add Rule
          </button>
        </div>

        {formData.customRules.length === 0 ? (
          <p className="text-sm text-gray-500 italic text-center py-4">No custom rules yet. Click "Add Rule" to create one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-300 bg-gray-50">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700 w-10">#</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700">Target Type</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700">Target</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700">Constraint</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700">Amount</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700">Period</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700">Meal Types</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {formData.customRules.map((rule, index) => (
                  <tr 
                    key={index} 
                    ref={el => ruleRefs.current[`rule-${index}`] = el}
                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-2 px-3 text-xs text-gray-600 font-medium">{index + 1}</td>
                    <td className="py-2 px-3">
                      <select
                        value={rule.targetType}
                        onChange={(e) => {
                          updateRule(index, 'targetType', e.target.value);
                          updateRule(index, 'target', '');
                          if (e.target.value === 'mealType') {
                            updateRule(index, 'mealTypes', []);
                          }
                        }}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      >
                        <option value="tag">Tag</option>
                        <option value="category">Category</option>
                        <option value="meatType">Meat Type</option>
                        <option value="recipe">Recipe</option>
                        <option value="mealType">Meal Type</option>
                      </select>
                    </td>
                    <td className="py-2 px-3">
                      {loadingTags && (rule.targetType === 'tag' || rule.targetType === 'recipe') ? (
                        <span className="text-xs text-gray-400">Loading...</span>
                      ) : (
                        <select
                          value={rule.target}
                          onChange={(e) => updateRule(index, 'target', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-amber-700 bg-white hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 min-w-[120px]"
                          required
                        >
                          <option value="">Select...</option>
                          {rule.targetType === 'recipe' ? (
                            getTargetOptions(rule.targetType).map(recipe => (
                              <option key={recipe._id} value={recipe._id}>{recipe.title}</option>
                            ))
                          ) : (
                            getTargetOptions(rule.targetType).map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))
                          )}
                        </select>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <select
                        value={rule.constraintType}
                        onChange={(e) => updateRule(index, 'constraintType', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      >
                        <option value="minimum">At Least</option>
                        <option value="maximum">At Most</option>
                        <option value="exact">Exactly</option>
                      </select>
                    </td>
                    <td className="py-2 px-3">
                      <input
                        type="number"
                        min="0"
                        value={rule.value}
                        onChange={(e) => updateRule(index, 'value', parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1.5 border border-gray-300 rounded-md text-sm font-semibold text-center focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        required
                      />
                    </td>
                    <td className="py-2 px-3">
                      <select
                        value={rule.period}
                        onChange={(e) => updateRule(index, 'period', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      >
                        <option value="week">Per Week</option>
                        <option value="month">Per Month</option>
                        <option value="year">Per Year</option>
                      </select>
                    </td>
                    <td className="py-2 px-3">
                      {rule.targetType !== 'mealType' ? (
                        <div className="flex flex-wrap gap-1.5">
                          {['breakfast', 'lunch', 'dinner', 'snack'].map(type => (
                            <label key={type} className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(rule.mealTypes || []).includes(type)}
                                onChange={(e) => {
                                  const currentMealTypes = rule.mealTypes || [];
                                  let newMealTypes;
                                  if (e.target.checked) {
                                    newMealTypes = [...currentMealTypes, type];
                                  } else {
                                    newMealTypes = currentMealTypes.filter(mt => mt !== type);
                                  }
                                  updateRule(index, 'mealTypes', newMealTypes);
                                }}
                                className="w-3.5 h-3.5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                              />
                              <span className="text-xs capitalize text-gray-600">{type.charAt(0)}</span>
                            </label>
                          ))}
                          {(rule.mealTypes || []).length === 0 && (
                            <span className="text-xs text-gray-400 italic">all</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <button
                        type="button"
                        onClick={() => removeRule(index)}
                        className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                        title="Remove rule"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!embedded && (
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Constraints'}
          </button>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return editorContent;
  }

  return (
    <form onSubmit={handleSubmit}>
      {editorContent}
    </form>
  );
}

export default Profile;

